import { AutosaveResult, RestoredDraft } from "../action-types";

// 같은 시각인가 — 조회(select)와 RPC 가 같은 timestamptz 를 다른 모양으로 줄 수 있어 Date 로도 비교한다.
export const sameInstant = (a: string | null, b: string | null): boolean => {
  if (a === null || b === null) return false;
  if (a === b) return true;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return !Number.isNaN(ta) && ta === tb;
};

// 두 시각 중 늦은 쪽 — 같은 순간이거나 하나라도 읽지 못하면 새 값(next), next 가 없으면 prev.
export const laterInstantOf = (prev: string | null, next: string | null): string | null => {
  if (next === null) return prev;
  if (prev === null) return next;
  const tp = Date.parse(prev);
  const tn = Date.parse(next);
  return Number.isNaN(tp) || Number.isNaN(tn) || tn >= tp ? next : prev;
};

// 이어쓸 초안이 어디서 왔나 — sessionStorage 의 떠나며 보낸 초안(pending) 또는 서버 초안.
export type RestoreSource = "pending" | "server";

export type RestoreDecision =
  | { kind: "none" }
  | { kind: "resume"; draft: RestoredDraft; source: RestoreSource }
  | { kind: "offer"; draft: RestoredDraft; source: RestoreSource; mine?: true };

// 서버를 거친 JSON 과 보낸 값이 같은가 — jsonb 는 키 순서를 바꾸므로 글자가 아니라 구조로 본다.
export const sameJson = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  const ka = Object.keys(ra).filter((k) => ra[k] !== undefined);
  const kb = Object.keys(rb).filter((k) => rb[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => k in rb && sameJson(ra[k], rb[k]));
};

// 서버 초안이 pending 을 남기고 떠날 때 그대로인가 — 떠날 때의 마지막 자동저장이거나, 떠나며 보낸 그 내용이 닿은 것.
const untouchedSince = (restored: RestoredDraft | null, pending: PendingDraft): boolean =>
  restored === null
    ? pending.prev === null
    : sameInstant(restored.saved_at, pending.prev) || sameJson(restored.data, pending.data);

// 이어쓰기 판정 한 곳 — pending 은 서버 초안이 떠날 때 그대로·번호도 같을 때만 묻지 않고 이어 쓰고 그 밖엔 배너, pending 이 없으면 서버 초안을 내 마지막 자동저장 시각으로 가른다.
export const restoreDecisionOf = (
  restored: RestoredDraft | null,
  ownSavedAt: string | null,
  currentRev: number,
  pending: PendingDraft | null = null
): RestoreDecision => {
  if (pending) {
    const draft = pendingAsDraft(pending);
    if (pending.base_rev !== currentRev || !untouchedSince(restored, pending)) {
      return { kind: "offer", draft, source: "pending", mine: true };
    }
    // 떠나며 보낸 것이 닿았으면 서버 초안(같은 내용·서버 시각)을, 아니면 pending 을 이어 쓴다.
    return restored && !sameInstant(restored.saved_at, pending.prev)
      ? { kind: "resume", draft: restored, source: "server" }
      : { kind: "resume", draft, source: "pending" };
  }
  if (!restored) return { kind: "none" };
  if (sameInstant(restored.saved_at, ownSavedAt)) {
    return restored.base_rev === currentRev
      ? { kind: "resume", draft: restored, source: "server" }
      : { kind: "offer", draft: restored, source: "server", mine: true };
  }
  const theirs = Date.parse(restored.saved_at);
  const mine = ownSavedAt === null ? Number.NaN : Date.parse(ownSavedAt);
  if (!Number.isNaN(theirs) && !Number.isNaN(mine) && theirs < mine) return { kind: "none" };
  return { kind: "offer", draft: restored, source: "server" };
};

// 배너로 물을 초안만 — 다른 탭·기기가 남긴 것, 또는 그 뒤 남이 저장한 내 초안.
export const offerOf = (
  restored: RestoredDraft | null,
  ownSavedAt: string | null,
  currentRev: number
): RestoredDraft | null => {
  const decision = restoreDecisionOf(restored, ownSavedAt, currentRev);
  return decision.kind === "offer" ? decision.draft : null;
};

const MY_DRAFT_KEY = "buildpy.my-draft.";

// 초안 자리는 사용자·견적마다 — 같은 탭에서 다른 계정으로 들어오면 앞사람 것이 보이지 않는다.
export const draftScopeOf = (memberId: string, quoteId: string): string => `${memberId}:${quoteId}`;

// 아래 함수들의 `scope` 는 draftScopeOf 로 만든 값이다.
export const myDraftKeyOf = (scope: string): string => MY_DRAFT_KEY + scope;

// 떠나면서 보낸(응답 전) 초안 — 서버에 닿기 전에 편집기가 다시 서면 이것이 가장 새 내용이다.
export interface PendingDraft {
  token: string;
  /** 보내기 직전까지 이 탭이 받은 마지막 자동저장 시각. */
  prev: string | null;
  base_rev: number;
  /** 보낸 시각(이 브라우저 시계) — 배너에 보여 줄 때만 쓴다. */
  at: string;
  data: unknown;
}

const PENDING_PREFIX = "{";

const readRaw = (scope: string): string | null => {
  try {
    return window.sessionStorage.getItem(myDraftKeyOf(scope));
  } catch {
    return null;
  }
};

const parsePending = (raw: string | null): PendingDraft | null => {
  if (raw === null || !raw.startsWith(PENDING_PREFIX)) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingDraft>;
    if (typeof value.token !== "string" || typeof value.base_rev !== "number" || typeof value.at !== "string") {
      return null;
    }
    return {
      token: value.token,
      prev: typeof value.prev === "string" ? value.prev : null,
      base_rev: value.base_rev,
      at: value.at,
      data: value.data,
    };
  } catch {
    return null;
  }
};

// 이 탭이 자동저장에 성공한 초안 시각 — 편집기가 다시 서도(앱 안 이동·새로고침) 이어진다. 못 읽으면 null.
export const loadMyDraft = (scope: string): string | null => {
  const raw = readRaw(scope);
  if (raw === null || !raw.startsWith(PENDING_PREFIX)) return raw;
  return parsePending(raw)?.prev ?? null;
};

export const loadPendingDraft = (scope: string): PendingDraft | null => parsePending(readRaw(scope));

// 이어쓸 수 있는 pending 만 — 깨졌거나(파싱) 편집기가 못 읽는 내용이면 지우고 null(서버 초안만으로 판정).
export const takePendingDraft = (scope: string, readable: (data: unknown) => boolean): PendingDraft | null => {
  const raw = readRaw(scope);
  if (raw === null || !raw.startsWith(PENDING_PREFIX)) return null;
  const pending = parsePending(raw);
  if (pending && readable(pending.data)) return pending;
  dropPendingDraft(scope);
  return null;
};

// 깨진 pending 에서도 prev 는 건진다 — 적는 순서가 token·prev 가 앞이라 뒤가 잘려도 대개 남는다.
const salvagePrev = (raw: string): string | null => /"prev":"([^"\\]+)"/.exec(raw)?.[1] ?? null;

// pending 내용만 버리고 「내 것」 표시(prev = 내 마지막 자동저장 시각, own 이 있으면 그것)는 남긴다 — 지우면 내 서버 초안이 남의 것처럼 배너로 다시 뜬다.
export const dropPendingDraft = (scope: string, own: string | null = null): void => {
  const raw = readRaw(scope);
  if (raw !== null && !raw.startsWith(PENDING_PREFIX)) {
    if (own !== null) saveMyDraft(scope, own);
    return;
  }
  const prev = own ?? (raw === null ? null : (parsePending(raw)?.prev ?? salvagePrev(raw)));
  if (prev !== null) saveMyDraft(scope, prev);
  else clearMyDraft(scope);
};

export const saveMyDraft = (scope: string, savedAt: string): void => {
  try {
    window.sessionStorage.setItem(myDraftKeyOf(scope), savedAt);
  } catch {
    // 못 적으면 돌아왔을 때 배너로 묻는다 — 잃는 것은 없다.
  }
};

// 못 적으면(용량·차단) 예전처럼 서버 초안으로 판정한다.
export const savePendingDraft = (scope: string, pending: PendingDraft): void => {
  try {
    window.sessionStorage.setItem(myDraftKeyOf(scope), JSON.stringify(pending));
  } catch {
    // 적지 못해도 보낸 요청은 그대로 간다.
  }
};

// 떠나며 보낸 초안의 응답(null = 서버가 받지 않음) — 그 사이 새 편집기가 자기 시각을 적었거나 다른 초안을 보냈으면 덮지 않는다.
export const ackPendingDraft = (scope: string, token: string, savedAt: string | null): void => {
  if (parsePending(readRaw(scope))?.token !== token) return;
  if (savedAt === null) dropPendingDraft(scope);
  else saveMyDraft(scope, savedAt);
};

// 떠나며 보낸 자동저장의 응답을 pending 에 반영 — 새 버전에 밀렸으면(superseded) 이어쓸 유일한 사본이라 남기고, 잠겼으면(locked) 이 견적으로는 회수할 곳이 없어 버린다(prev 는 남김).
export const ackPendingResult = (scope: string, token: string, result: AutosaveResult): void => {
  if (result.ok) ackPendingDraft(scope, token, result.saved_at);
  else if (result.message === "" && "superseded" in result && !result.superseded) ackPendingDraft(scope, token, null);
};

// 받지 않은 자동저장을 화면에 어떻게 알릴지 — locked(발송 등)·superseded(새 버전)·silent(늦게 닿은 회차)·failed(오류 문구).
export type AutosaveSkip = "locked" | "superseded" | "silent" | "failed";

export const autosaveSkipOf = (result: Exclude<AutosaveResult, { ok: true }>): AutosaveSkip => {
  if (result.message !== "") return "failed";
  if ("locked" in result && result.locked) return "locked";
  return "superseded" in result && result.superseded ? "superseded" : "silent";
};

// 자동저장이 막힌 까닭 — locked(발송 등) · superseded(같은 계정의 다른 탭 저장으로 이 탭 번호로는 안 적힘) · failed(마지막 자동저장이 실패한 뒤 새 편집이 없음).
export type AutosaveBlock = "locked" | "superseded" | "failed" | null;

// 막힌 뒤의 자동저장 — locked 는 보내지도 적지도 않고(편집 내용 복사로 옮긴다), superseded 는 보내지 않되 떠날 때 저장 안 된 편집을 pending 으로, failed 는 평소대로 두되 떠날 때 받지 못한 편집을 pending 으로 적는다.
export const autosavePlanOf = (block: AutosaveBlock, leaving: boolean, dirty: boolean): "send" | "record" | "skip" => {
  if (block === null || (block === "failed" && !leaving)) return "send";
  if (block === "locked") return "skip";
  return leaving && dirty ? "record" : "skip";
};

// 되돌리기로 저장본에 닿았을 때 서버 초안을 지울지 — 이 탭이 적은 초안(응답 전 포함)이 있고 배너·잠김·밀림이 없을 때만(남기면 다시 열 때 되돌리기 전 편집이 되살아난다).
export const dropDraftOnCleanOf = (s: {
  wasDirty: boolean;
  dirty: boolean;
  ownDraft: boolean;
  inflight: boolean;
  offer: boolean;
  block: AutosaveBlock;
}): boolean =>
  s.wasDirty && !s.dirty && (s.ownDraft || s.inflight) && !s.offer && (s.block === null || s.block === "failed");

// 버리기 — 앞서 보낸 자동저장 응답부터 기다린 뒤 지운다(늦게 닿은 자동저장이 지운 초안을 되살리지 않게). 그 응답이 적은 시각을 넘기고, 던지면 null.
export const discardAfterFlight = async <T, F = void>(
  flight: Promise<F> | undefined,
  discard: (flown: F | undefined) => Promise<T>
): Promise<T | null> => {
  const flown = await flight;
  return discard(flown).catch(() => null);
};

// 되돌리기 삭제의 비교 기준 — 기다린 자동저장이 적은 시각(세대가 바뀌어 ownSavedAt 에 안 들어간다)과 이 탭의 마지막 자동저장 중 늦은 쪽.
export const revertExpectOf = (flown: string | null | undefined, own: string | null): string | null =>
  laterInstantOf(own, flown ?? null);

// 되돌리기 삭제 결과 — cleared(지움) · kept(슬롯이 그 뒤 다른 탭 편집이라 남김) · failed(요청 실패).
export const revertOutcomeOf = (
  result: { ok: true; cleared?: boolean } | { ok: false; message: string } | null
): "cleared" | "kept" | "failed" => {
  if (!result || !result.ok) return "failed";
  return result.cleared === false ? "kept" : "cleared";
};

// 자동저장 응답을 화면에 반영할지 — 낡은 회차(저장·버리기 뒤)라도 잠김·밀림은 사실이라 반영하되, 내 저장으로 번호가 오른 뒤의 밀림은 내 저장 탓이라 버린다.
export const flightSkipOf = (
  skip: AutosaveSkip,
  stale: boolean,
  sentRev: number,
  currentRev: number
): AutosaveSkip | null => {
  if (!stale || skip === "locked") return skip;
  return skip === "superseded" && sentRev === currentRev ? skip : null;
};

// 떠나며 보낸 초안을 다시 설 편집기가 받을 모양으로 — 서버 초안과 같은 판정을 거친다.
export const pendingAsDraft = (pending: PendingDraft): RestoredDraft => ({
  data: pending.data,
  saved_at: pending.at,
  base_rev: pending.base_rev,
});

export const clearMyDraft = (scope: string): void => {
  try {
    window.sessionStorage.removeItem(myDraftKeyOf(scope));
  } catch {
    // 지우지 못해도 서버 초안이 없으면 쓰이지 않는다.
  }
};

export const SUPERSEDED_NOTE = "다른 곳에서 저장된 새 버전이 있습니다 — 새로고침해 확인하세요";

// 제목 옆 저장 표시 — 새 버전에 밀려 자동저장이 적히지 않았으면(superseded) 「자동저장됨」 이라 하지 않고, 잠겼으면(locked) 편집이 없어도 「자동저장 안 됨」 을 붙인다.
export const autosaveLabelOf = (s: {
  dirty: boolean;
  autosavedAt: string | null;
  failed: boolean;
  superseded: boolean;
  locked?: boolean;
}): string => {
  if (s.locked) return s.dirty ? " · 저장 안 됨 (자동저장 안 됨)" : " · 고칠 수 없음 (자동저장 안 됨)";
  if (!s.dirty) return "";
  if (s.superseded) return " · 저장 안 됨 (자동저장 안 됨)";
  return s.autosavedAt && !s.failed ? " · 저장 안 됨 (자동저장됨)" : " · 저장 안 됨";
};

// 폰 머리줄 저장 표시 — 좁은 폭이라 괄호 없이 한 말로 줄인다(데스크톱은 autosaveLabelOf 그대로).
export const phoneAutosaveLabelOf = (s: Parameters<typeof autosaveLabelOf>[0]): string => {
  if (s.locked) return s.dirty ? " · 저장 안 됨" : " · 고칠 수 없음";
  if (!s.dirty) return "";
  if (s.superseded) return " · 저장 안 됨";
  return s.autosavedAt && !s.failed ? " · 임시 저장됨" : " · 저장 안 됨";
};
